import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity } from "@/components/icons";
import { Card } from "@/components/ui/card";
import { useCalendarQuery } from "./useCalendarQuery";
import { addDays, mondayOfIsoWeek, severityCount, ymdInTz } from "./dateUtils";
import { WeekNav } from "./WeekNav";
import { TimezoneSelect, CALENDAR_TZ_STORAGE_KEY } from "./TimezoneSelect";
import { FilterBar } from "./FilterBar";
import { DayRail } from "./DayRail";
import { AgendaList } from "./AgendaList";
import { EventPanel } from "./EventPanel";
import { pickDefaultEventId } from "./defaultSelection";
import type {
  CalendarEventType,
  CalendarImportance,
} from "./types";
import { useLanguage } from "@/i18n/useLanguage";

export const CALENDAR_FILTERS_STORAGE_KEY = "calendar.filters.v1";
const FILTERS_KEY = CALENDAR_FILTERS_STORAGE_KEY;
const KNOWN_MATERIALS = ["XAUUSD", "WTI", "BRENT", "BTCUSD", "EURUSD", "GBPUSD", "USDJPY", "DXY"];

// The week grid, "today" highlight, and agenda grouping all need to follow the
// user's selected tz — not UTC — or users far from UTC see the wrong week
// around midnight. We anchor the user's local calendar date at UTC midnight so
// the existing UTC date math (mondayOfIsoWeek, addDays, .toISOString().slice)
// keeps working unchanged.
function localToday(tz: string): Date {
  return new Date(`${ymdInTz(new Date(), tz)}T00:00:00Z`);
}

interface StoredFilters {
  materials: string[];
  importance: CalendarImportance[];
  types: CalendarEventType[];
}

function readFilters(): StoredFilters {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (raw) return JSON.parse(raw) as StoredFilters;
  } catch {
    /* fall through to defaults */
  }
  return {
    materials: KNOWN_MATERIALS,
    importance: ["high", "medium"],
    types: [
      "econ_release",
      "central_bank",
      "gov_publication",
      "exec_schedule",
      "gov_release",
      "holiday",
      "other",
    ],
  };
}

export function CalendarPage() {
  const { t } = useTranslation("calendar");
  const { language } = useLanguage();
  const [tz, setTz] = useState<string>(
    () => localStorage.getItem(CALENDAR_TZ_STORAGE_KEY) ?? "America/New_York",
  );
  const [start, setStart] = useState<Date>(() => mondayOfIsoWeek(localToday(tz)));
  const [filters, setFilters] = useState<StoredFilters>(readFilters);
  // EventPanel is now a permanent column — selectedId stays a string so the
  // detail view is always populated. Reconciled in an effect below whenever
  // the event list changes (filter, week, or initial load).
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const end = useMemo(() => addDays(start, 6), [start]);

  const query = useCalendarQuery({
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
    materials: filters.materials.length ? filters.materials : undefined,
    importance: filters.importance.length ? filters.importance : undefined,
    types: filters.types.length ? filters.types : undefined,
    // i18n Slice 2 — request the user's preferred language; the
    // response's `language_served` drives the fallback badge below.
    lang: language,
  });

  const events = query.data?.events ?? [];

  // Persist filters whenever they change.
  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  // Default-select the next upcoming event whenever the visible list
  // changes and the current selection isn't in it (initial load, week
  // change, filter change). If everything in view is already past, fall
  // back to the latest event so the panel is always populated.
  useEffect(() => {
    if (events.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId === null || !events.some((e) => e.id === selectedId)) {
      setSelectedId(pickDefaultEventId(events, new Date()));
    }
  }, [events, selectedId]);

  const today = localToday(tz);
  const siblingIds = useMemo(() => events.map((e) => e.id), [events]);

  const countsByDate = useMemo(() => {
    const acc: Record<string, ReturnType<typeof severityCount>> = {};
    for (const ev of events) {
      const list = acc[ev.scheduled_date] ?? { high: 0, medium: 0, low: 0, none: 0 };
      list[ev.importance] += 1;
      acc[ev.scheduled_date] = list;
    }
    return acc;
  }, [events]);

  const pastResultsByDate = useMemo(() => {
    const acc: Record<string, { beats: number; misses: number }> = {};
    for (const ev of events) {
      if (ev.status !== "occurred" && ev.status !== "revised") continue;
      if (!ev.actual || !ev.forecast) continue;
      const a = parseFloat(ev.actual.replace(/[^\d.\-]/g, ""));
      const f = parseFloat(ev.forecast.replace(/[^\d.\-]/g, ""));
      if (isNaN(a) || isNaN(f)) continue;
      const cur = acc[ev.scheduled_date] ?? { beats: 0, misses: 0 };
      if (a > f) cur.beats += 1;
      else if (a < f) cur.misses += 1;
      acc[ev.scheduled_date] = cur;
    }
    return acc;
  }, [events]);

  const dates = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < 7; i++) out.push(addDays(start, i));
    return out;
  }, [start]);

  const past = events.filter(
    (e) => e.status === "occurred" || e.status === "revised",
  ).length;
  const upcoming = events.length - past;

  return (
    <div className="flex h-full flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-3 border-b pb-2">
        <div className="flex items-center gap-3">
          <h1 className="text-base font-semibold">{t("page.title")}</h1>
          <WeekNav
            start={start}
            end={end}
            onPrev={() => setStart(addDays(start, -7))}
            onNext={() => setStart(addDays(start, 7))}
            onToday={() => setStart(mondayOfIsoWeek(localToday(tz)))}
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <TimezoneSelect value={tz} onChange={setTz} />
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <Activity className="h-3 w-3" aria-hidden="true" />
            {t("page.live")}
          </span>
        </div>
      </div>

      <FilterBar
        materials={KNOWN_MATERIALS}
        selectedMaterials={filters.materials}
        selectedImportance={filters.importance}
        selectedTypes={filters.types}
        counts={{ total: events.length, past, upcoming }}
        onChange={(next) =>
          setFilters((prev) => ({
            materials: next.materials ?? prev.materials,
            importance: next.importance ?? prev.importance,
            types: next.types ?? prev.types,
          }))
        }
      />

      <div className="flex flex-1 gap-3 overflow-hidden">
        <DayRail
          dates={dates}
          today={today}
          counts={countsByDate}
          pastResults={pastResultsByDate}
          onSelect={(iso) => {
            // Scroll the matching agenda section into view. Lives in the
            // page rather than inside AgendaList to avoid threading a ref
            // through; the section id is owned by AgendaList.
            document
              .getElementById(`agenda-day-${iso}`)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
        <AgendaList
          events={events}
          today={today}
          tz={tz}
          selectedEventId={selectedId}
          onSelect={setSelectedId}
          emptyDays={dates.map((d) => d.toISOString().slice(0, 10))}
        />
        {selectedId ? (
          <EventPanel
            eventId={selectedId}
            siblingIds={siblingIds}
            tz={tz}
            onSelect={setSelectedId}
            requestedLanguage={language}
            servedLanguage={query.data?.language_served ?? null}
          />
        ) : (
          // Reserve the column when there's no selection so the agenda
          // doesn't widen and snap back. Two states: filters excluded
          // everything (friendly explainer), or the brief gap between
          // filter change and re-default (silent placeholder).
          <Card className="flex w-80 shrink-0 flex-col items-center justify-center gap-2 p-6 text-center text-xs text-muted-foreground lg:w-[28rem]">
            {events.length === 0 ? (
              <>
                <p className="font-medium text-foreground/80">
                  {t("page.noMatches")}
                </p>
                <p>{t("page.noMatchesHint")}</p>
              </>
            ) : (
              <p>{t("page.selectPrompt")}</p>
            )}
          </Card>
        )}
      </div>

      {query.error && (
        <div className="text-xs text-red-400">{t("page.loadError")}</div>
      )}
    </div>
  );
}
